import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import {
  ConfirmedVulnerabilityFindingStateSchema,
  SignalCategorySchema,
  SignalStageSchema,
  SignalStatusSchema,
} from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';
import {
  SignalCategoryDistributionSchema,
  SignalQualityDistributionSchema,
  SignalTrendsOverTimeSchema,
  TopEndpointsBySignalsQuerySchema,
  TopEndpointsBySignalsSchema,
} from './signals.schema';

export const signalsAnalyticsProcedures = {
  getQualityDistribution: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/metrics/quality-distribution',
      summary: 'Get quality distribution',
      description:
        'Returns the distribution of signals by quality label (HOT, COLD, PASSED, etc.) across all applications. Used for dashboard pie charts.',
      inputStructure: 'detailed',
    })
    .output(SignalQualityDistributionSchema),
  getQualityDistributionByApplicationId: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/metrics/quality-distribution/application/{applicationId}',
      summary: 'Get quality distribution by application',
      description:
        'Returns signal quality distribution for a specific application. Useful for application-specific dashboards.',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ applicationId: z.string() }),
        query: z.object({
          category: SignalCategorySchema.array()
            .optional()
            .describe('Filter by signal categories'),
          status: SignalStatusSchema.array()
            .optional()
            .describe('Filter by signal statuses'),
          confirmedVulnerabilityFindingState:
            ConfirmedVulnerabilityFindingStateSchema.array()
              .optional()
              .describe(
                'Filter confirmed vulnerability signals by finding state',
              ),
        }),
      }),
    )
    .output(SignalQualityDistributionSchema),
  getCategoryDistribution: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/metrics/category-distribution',
      summary: 'Get category distribution',
      description:
        'Returns the distribution of signals by vulnerability category (XSS, SQL Injection, etc.) across all applications.',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        query: z.object({
          from: z
            .string()
            .optional()
            .describe('Start date for the distribution period (ISO string)'),
          to: z
            .string()
            .optional()
            .describe('End date for the distribution period (ISO string)'),
          environment: z.string().optional().describe('Filter by environment'),
          businessUnitId: z
            .string()
            .optional()
            .describe('Filter by business unit ID'),
          applicationId: z
            .string()
            .optional()
            .describe('Filter by application ID'),
          targetId: z.string().optional().describe('Alias for application ID'),
          labels: SignalStageSchema.array()
            .optional()
            .describe('Deprecated alias for stage filter'),
          stages: SignalStageSchema.array()
            .optional()
            .describe('Filter by signal stages'),
          statuses: SignalStatusSchema.array()
            .optional()
            .describe('Filter by signal statuses'),
          confirmedVulnerabilityFindingState:
            ConfirmedVulnerabilityFindingStateSchema.array()
              .optional()
              .describe(
                'Filter confirmed vulnerability signals by finding state',
              ),
        }),
      }),
    )
    .output(SignalCategoryDistributionSchema),
  getTopEndpointsBySignals: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/metrics/top-endpoints-by-signals',
      summary: 'Get top endpoints by signals',
      description:
        'Returns top endpoints ranked by total signals count. Each endpoint includes signal label buckets and counts.',
      inputStructure: 'detailed',
    })
    .input(z.object({ query: TopEndpointsBySignalsQuerySchema }))
    .output(TopEndpointsBySignalsSchema),
  getCategoryDistributionByApplicationId: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/metrics/category-distribution/application/{applicationId}',
      summary: 'Get category distribution by application',
      description:
        'Returns signal category distribution for a specific application.',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ applicationId: z.string() }),
        query: z.object({
          category: SignalCategorySchema.array()
            .optional()
            .describe('Filter by signal categories'),
          status: SignalStatusSchema.array()
            .optional()
            .describe('Filter by signal statuses'),
          confirmedVulnerabilityFindingState:
            ConfirmedVulnerabilityFindingStateSchema.array()
              .optional()
              .describe(
                'Filter confirmed vulnerability signals by finding state',
              ),
        }),
      }),
    )
    .output(SignalCategoryDistributionSchema),
  getSignalGenerationTrends: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/metrics/trends',
      summary: 'Get signal trends',
      description:
        'Returns signal generation trends over time showing how many signals of each quality were generated per month. Useful for tracking security testing progress.',
      inputStructure: 'detailed',
    })
    .output(SignalTrendsOverTimeSchema),
  getSignalGenerationTrendsByApplicationId: terraContract
    .meta({
      permissions: [PERMISSIONS.VIEW_TESTS.name],
    })
    .route({
      method: 'GET',
      path: '/metrics/trends/application/{applicationId}',
      summary: 'Get signal trends by application',
      description:
        'Returns signal generation trends for a specific application over time.',
      inputStructure: 'detailed',
    })
    .input(
      z.object({
        params: z.object({ applicationId: z.string() }),
        query: z.object({
          category: SignalCategorySchema.array()
            .optional()
            .describe('Filter by signal categories'),
          status: SignalStatusSchema.array()
            .optional()
            .describe('Filter by signal statuses'),
          confirmedVulnerabilityFindingState:
            ConfirmedVulnerabilityFindingStateSchema.array()
              .optional()
              .describe(
                'Filter confirmed vulnerability signals by finding state',
              ),
        }),
      }),
    )
    .output(SignalTrendsOverTimeSchema),
};
